import { PROTECOES_INICIAIS } from '../didatico/manejoVidros'
import type { CenaTabletop, ObjetoTabletop, PosicaoFinalVeiculo, TipoObjetoTabletop, TransformacaoTabletop } from './tipos'

const TIPOS_V1 = new Set<string>([
  'carro',
  'suv',
  'moto',
  'caminhao',
  'arvore',
  'poste',
  'defensa',
  'cone',
  'bloco',
  'vitima',
  'vidro',
  'detritosVidro',
])

interface ObjetoV1 {
  id: string
  tipo: string
  nome: string
  transformacao: TransformacaoTabletop
  cor?: string
}

interface CenaV1 {
  versao: 1
  nome: string
  objetos: readonly ObjetoV1[]
}

const ehRegistro = (valor: unknown): valor is Record<string, unknown> =>
  typeof valor === 'object' && valor !== null && !Array.isArray(valor)

const ehVetor3 = (valor: unknown): valor is readonly [number, number, number] =>
  Array.isArray(valor) && valor.length === 3 && valor.every((v) => typeof v === 'number' && Number.isFinite(v))

const ehTransformacaoV1 = (valor: unknown): valor is TransformacaoTabletop =>
  ehRegistro(valor) &&
  ehVetor3(valor.posicao) &&
  ehVetor3(valor.rotacao) &&
  ehVetor3(valor.escala) &&
  valor.escala.every((c) => c > 0)

const ehObjetoV1 = (valor: unknown): valor is ObjetoV1 =>
  ehRegistro(valor) &&
  typeof valor.id === 'string' &&
  typeof valor.tipo === 'string' &&
  TIPOS_V1.has(valor.tipo) &&
  typeof valor.nome === 'string' &&
  ehTransformacaoV1(valor.transformacao) &&
  (valor.cor === undefined || typeof valor.cor === 'string')

/** Reconhece um arquivo/estado salvo do antigo Diorama 3D (v1), antes de tentar validar como v2. */
export function ehCenaV1(valor: unknown): valor is CenaV1 {
  return (
    ehRegistro(valor) &&
    valor.versao === 1 &&
    typeof valor.nome === 'string' &&
    Array.isArray(valor.objetos) &&
    valor.objetos.every(ehObjetoV1)
  )
}

const DOIS_PI = Math.PI * 2
/** Normaliza um ângulo para (-π, π], para comparar rotações independente da volta dada. */
function normalizarAngulo(rad: number): number {
  let a = rad % DOIS_PI
  if (a > Math.PI) a -= DOIS_PI
  if (a <= -Math.PI) a += DOIS_PI
  return a
}

const PROXIMO = (a: number, alvo: number, tolerancia = Math.PI / 4) =>
  Math.abs(normalizarAngulo(a - alvo)) <= tolerancia

/**
 * Infere a posição final de um veículo v1 a partir do roll (rotação em Z),
 * já que o v1 não tinha o conceito de "como o carro parou" — só um gizmo
 * livre. Sem rolagem reconhecível, assume-se sobre as rodas.
 */
export function inferirPosicaoFinal(transformacao: TransformacaoTabletop): PosicaoFinalVeiculo {
  const roll = normalizarAngulo(transformacao.rotacao[2])
  if (PROXIMO(roll, Math.PI) || PROXIMO(roll, -Math.PI)) return 'capotado'
  if (PROXIMO(roll, Math.PI / 2)) return 'tombadoDireito'
  if (PROXIMO(roll, -Math.PI / 2)) return 'tombadoEsquerdo'
  return 'sobreRodas'
}

function migrarObjetoV1(objeto: ObjetoV1): ObjetoTabletop {
  const base: ObjetoTabletop = {
    id: objeto.id,
    tipo: objeto.tipo as TipoObjetoTabletop,
    nome: objeto.nome,
    transformacao: objeto.transformacao,
    ...(objeto.cor === undefined ? {} : { cor: objeto.cor }),
  }

  if (objeto.tipo === 'carro' || objeto.tipo === 'suv') {
    return {
      ...base,
      veiculo: {
        idPerfil: objeto.tipo === 'suv' ? 'sedanMedio' : 'hatchCompacto',
        posicaoFinal: inferirPosicaoFinal(objeto.transformacao),
        estadosPecas: {},
        vidros: {},
        insulfilm: false,
        protecoes: { ...PROTECOES_INICIAIS },
      },
    }
  }

  if (objeto.tipo === 'vitima') {
    // o v1 não guardava perfil nem gravidade: entra como estável/amarela, no
    // solo, na mesma pose em que o aluno a deixou — o instrutor reclassifica
    // pelo inspetor depois da migração.
    return {
      ...base,
      vitima: {
        idPerfil: 'adultoMasculino',
        triagem: 'amarelo',
        colarCervical: false,
        local: { tipo: 'solo', postura: 'decubitoDorsal' },
      },
    }
  }

  return base
}

export function migrarCenaV1ParaV2(cena: CenaV1): CenaTabletop {
  return {
    versao: 2,
    nome: cena.nome,
    objetos: cena.objetos.map(migrarObjetoV1),
  }
}
